import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api } from './api.js';
import { navigate } from './router.js';
import { Icon } from './shell.js';
import { useLang, useT } from './i18n.js';

/**
 * Notifications (owner request 2026-10-17: see what happens without being flooded). The bell in the top bar lists what
 * concerns you; a few things that cannot wait (a mention, the client's answer, a meeting about to start) also show as
 * a small box in the corner. Guards: at most three boxes an hour (meeting reminders aside), none outside 07–19 on
 * weekdays, none while you sit in a meeting (a summary comes afterwards), at most two at a time. Opening the page a
 * notification points to marks it read.
 *
 * Meeting and shoot reminders show as a banner at the top of the window, and as a system notification from the browser
 * once the person has allowed it (owner request 2026-10-19: a ping you really notice). The system notification only
 * comes while a Hub tab is open, also in the background; Open Meet and the snooze work from it too.
 */

export interface Notice {
  id: string;
  kind: string;
  level: 'now' | 'bell';
  text: string;
  where: string;
  link: string | null;
  at: string;
  read: boolean;
}
export interface AgendaItem {
  kind: 'meeting' | 'shoot';
  id: string;
  title: string;
  start: string;
  end: string;
  meet?: string;
  link?: string;
}

export const POLL_MS = 60_000;
export const AGENDA_MS = 10 * 60_000;
/** owner decision 2026-10-17: 10 minutes before a meeting */
export const MEETING_LEAD_MIN = 10;
export const SHOOT_LEAD_MIN = 60;
export const MAX_PER_HOUR = 3;
export const MAX_VISIBLE = 2;
export const SHOW_MS = 8_000;

/* ---------------- rules (pure, tested) ---------------- */

const stockholm = (now: Date) => {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Stockholm', weekday: 'short', hour: '2-digit', hourCycle: 'h23' }).formatToParts(now);
  return { dow: parts.find((x) => x.type === 'weekday')?.value ?? '', hour: Number(parts.find((x) => x.type === 'hour')?.value ?? 0) };
};

/** Boxes only on weekdays 07–19 Stockholm; the bell collects everything anyway. */
export function workHours(now: Date): boolean {
  const s = stockholm(now);
  return s.dow !== 'Sat' && s.dow !== 'Sun' && s.hour >= 7 && s.hour < 19;
}

export const inMeeting = (agenda: AgendaItem[], now: Date): boolean => agenda.some((a) => a.kind === 'meeting' && Date.parse(a.start) <= now.getTime() && now.getTime() < Date.parse(a.end));

/** The reminders due now: a meeting 10 minutes before, a shoot an hour before, each once (a snooze brings it back later). */
export function dueReminders(agenda: AgendaItem[], now: Date, done: Record<string, number>): { item: AgendaItem; key: string; min: number }[] {
  const t = now.getTime();
  const out: { item: AgendaItem; key: string; min: number }[] = [];
  for (const a of agenda) {
    const start = Date.parse(a.start);
    const lead = (a.kind === 'shoot' ? SHOOT_LEAD_MIN : MEETING_LEAD_MIN) * 60_000;
    const key = `${a.kind}|${a.id}|${a.start}`;
    const next = done[key];
    if (start <= t || start - t > lead) continue;
    if (next !== undefined && (next === 0 || t < next)) continue;
    out.push({ item: a, key, min: Math.max(1, Math.ceil((start - t) / 60_000)) });
  }
  return out;
}

/**
 * Which new notifications become boxes: unread, level now, newer than the last look, within the hourly limit. The
 * rest stay in the bell. `held` counts those kept back because of a meeting (or work hours: those are simply dropped).
 */
export function pickBoxes(items: Notice[], seenAt: string, shown: number[], now: Date, busy: boolean): { show: Notice[]; held: number; seenAt: string } {
  const fresh = items.filter((n) => !n.read && n.level === 'now' && n.at > seenAt);
  const newest = items.reduce((m, n) => (n.at > m ? n.at : m), seenAt);
  if (!fresh.length) return { show: [], held: 0, seenAt: newest };
  if (!workHours(now)) return { show: [], held: 0, seenAt: newest };
  if (busy) return { show: [], held: fresh.length, seenAt: newest };
  const lastHour = shown.filter((x) => now.getTime() - x < 3600_000).length;
  return { show: fresh.slice(0, Math.max(0, Math.min(MAX_VISIBLE, MAX_PER_HOUR - lastHour))), held: 0, seenAt: newest };
}

/** Unread notifications that point at this page. */
export const readHere = (items: Notice[], path: string): string[] => items.filter((n) => !n.read && n.link && n.link.split('?')[0] === path).map((n) => n.id);

/* ---------------- the browser's own notification ---------------- */

/** on = allowed, off = not asked yet, blocked = said no in the browser, none = this browser has none (or it is not ours to ask) */
export type PingState = 'on' | 'off' | 'blocked' | 'none';
export const pingState = (): PingState => {
  if (typeof Notification === 'undefined') return 'none';
  return Notification.permission === 'granted' ? 'on' : Notification.permission === 'denied' ? 'blocked' : 'off';
};
const SW = '/timer-sw.js';
const pingTag = (key: string) => `kff-meeting|${key}`;

/** A system notification for a reminder; through the service worker where there is one (phones need it, and it gives the buttons). */
async function ping(r: { key: string; item: AgendaItem }, title: string, body: string, acts: { meet: string; snooze: string }) {
  if (pingState() !== 'on') return;
  const tag = pingTag(r.key);
  const data = { type: 'kff-meeting', key: r.key, meet: r.item.meet ?? null };
  try {
    const reg = await navigator.serviceWorker?.register(SW);
    if (reg) {
      await reg.showNotification(title, {
        body,
        tag,
        data,
        icon: '/favicon.svg',
        requireInteraction: true,
        actions: [...(r.item.meet ? [{ action: 'meet', title: acts.meet }] : []), { action: 'snooze', title: acts.snooze }]
      } as NotificationOptions);
      return;
    }
  } catch {
    /* no service worker here: a plain notification still pings */
  }
  try {
    const n = new Notification(title, { body, tag, icon: '/favicon.svg', requireInteraction: true });
    n.onclick = () => {
      window.focus();
      if (r.item.meet) window.open(r.item.meet, '_blank', 'noopener');
      n.close();
    };
  } catch {
    /* the banner at the top is still there */
  }
}
async function unping(key: string) {
  try {
    const reg = await navigator.serviceWorker?.getRegistration(SW);
    for (const n of (await reg?.getNotifications({ tag: pingTag(key) })) ?? []) n.close();
  } catch {
    /* nothing to close */
  }
}

/* ---------------- memory on this device ---------------- */

const KEY = 'kff.notify';
interface Memo {
  seenAt?: string;
  shown?: number[];
  /** reminder key → 0 shown and closed, or the time a snooze brings it back */
  done?: Record<string, number>;
}
const readMemo = (): Memo => {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Memo;
  } catch {
    return {};
  }
};
const writeMemo = (m: Memo) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(m));
  } catch {
    /* private mode: remembered until the page closes */
  }
};

/* ---------------- the bell and the corner ---------------- */

type Box =
  | { key: string; kind: 'notice'; n: Notice }
  | { key: string; kind: 'reminder'; item: AgendaItem; min: number; reminder: string }
  | { key: string; kind: 'held'; count: number };

const dayGroup = (iso: string, now: Date): 'today' | 'yesterday' | 'earlier' => {
  const day = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm' }).format(d);
  const d = day(new Date(iso));
  if (d === day(now)) return 'today';
  if (d === day(new Date(now.getTime() - 86_400_000))) return 'yesterday';
  return 'earlier';
};

/**
 * `corner`: boxes and meeting reminders (staff); off on the freelancer link, which only has the bell (owner decision
 * 2026-10-17). `follow`: a click opens the notification's page (staff app); on the link it only marks it read, or hands
 * the link to `onLink` (the crew's review opens on the link).
 */
export function Notifications(p: { api: Api; corner: boolean; follow: boolean; onLink?: (link: string) => void }) {
  const t = useT();
  const lang = useLang();
  const [items, setItems] = useState<Notice[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const agenda = useRef<AgendaItem[]>([]);
  const held = useRef(0);
  const memo = useRef<Memo>(readMemo());
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const itemsRef = useRef<Notice[]>([]);
  const [pingNow, setPingNow] = useState<PingState>(pingState);
  const tRef = useRef(t);
  tRef.current = t;

  const save = (patch: Partial<Memo>) => {
    memo.current = { ...memo.current, ...patch };
    writeMemo(memo.current);
  };
  const apply = (r: { items: Notice[]; unread: number }) => {
    itemsRef.current = r.items;
    setItems(r.items);
    setUnread(r.unread);
    setFailed(false);
  };
  const markRead = async (ids: string[] | 'all') => {
    if (ids !== 'all' && !ids.length) return;
    try {
      apply(await p.api.post<{ items: Notice[]; unread: number }>('/api/notifications/read', ids === 'all' ? { all: true } : { ids }));
    } catch {
      /* stays unread; the next look tries again */
    }
  };
  const push = (b: Box) => setBoxes((cur) => (cur.some((x) => x.key === b.key) ? cur : [...cur, b].slice(-MAX_VISIBLE - 1)));
  const drop = (key: string) => setBoxes((cur) => cur.filter((x) => x.key !== key));
  const snooze = (key: string) => {
    drop(key);
    void unping(key);
    save({ done: { ...(memo.current.done ?? {}), [key]: Date.now() + 5 * 60_000 } });
  };
  /** asked on a click (browsers only allow that), from the banner or the bell */
  const allowPing = async () => {
    try {
      await Notification.requestPermission();
    } catch {
      /* stays as it was */
    }
    setPingNow(pingState());
  };

  const load = async () => {
    let r: { items: Notice[]; unread: number };
    try {
      r = await p.api.get<{ items: Notice[]; unread: number }>('/api/notifications');
    } catch {
      setFailed(true);
      return;
    }
    apply(r);
    // the page you are on counts as read
    const here = readHere(r.items, location.pathname);
    if (here.length && p.follow) void markRead(here);
    if (!p.corner) return;
    const now = new Date();
    // the first look on this device only sets the mark: what came before is in the bell
    if (!memo.current.seenAt) return save({ seenAt: r.items[0]?.at ?? now.toISOString() });
    const busy = inMeeting(agenda.current, now);
    const pick = pickBoxes(r.items.filter((n) => !here.includes(n.id)), memo.current.seenAt, memo.current.shown ?? [], now, busy);
    held.current += pick.held;
    if (!busy && held.current > 0) {
      push({ key: `held-${now.getTime()}`, kind: 'held', count: held.current });
      held.current = 0;
    }
    for (const n of pick.show) push({ key: n.id, kind: 'notice', n });
    save({ seenAt: pick.seenAt, shown: [...(memo.current.shown ?? []).filter((x) => now.getTime() - x < 3600_000), ...pick.show.map(() => now.getTime())] });
  };

  const loadAgenda = async () => {
    try {
      agenda.current = (await p.api.get<{ items: AgendaItem[] }>('/api/notifications/agenda')).items;
    } catch {
      /* no reminders until the next look */
    }
  };
  const remind = () => {
    const now = new Date();
    const done = memo.current.done ?? {};
    for (const r of dueReminders(agenda.current, now, done)) {
      push({ key: r.key, kind: 'reminder', item: r.item, min: r.min, reminder: r.key });
      done[r.key] = 0;
      const tx = tRef.current.notify;
      void ping(r, r.item.kind === 'shoot' ? tx.shootStartsIn(r.item.title, r.min) : tx.startsIn(r.item.title, r.min), `${clock(r.item.start)}–${clock(r.item.end)}`, { meet: tx.openMeet, snooze: tx.snooze });
    }
    // forget reminders for meetings that are over
    for (const k of Object.keys(done)) if (Date.parse(k.slice(k.lastIndexOf('|') + 1)) < now.getTime() - 86_400_000) delete done[k];
    save({ done });
  };

  useEffect(() => {
    void load();
    const look = () => document.visibilityState === 'visible' && void load();
    const poll = setInterval(look, POLL_MS);
    document.addEventListener('visibilitychange', look);
    // opening a page marks what points at it read
    const onRoute = () => p.follow && void markRead(readHere(itemsRef.current, location.pathname));
    addEventListener('popstate', onRoute);
    let agendaPoll: ReturnType<typeof setInterval> | undefined;
    let tick: ReturnType<typeof setInterval> | undefined;
    // an answer given in the system notification (the worker passes it on)
    const onAnswer = (e: MessageEvent) => {
      const m = e.data as { type?: string; action?: string; key?: string } | null;
      if (m?.type !== 'kff-meeting' || !m.key) return;
      if (m.action === 'snooze') snooze(m.key);
      else drop(m.key);
    };
    if (p.corner) {
      void loadAgenda().then(remind);
      // also in a tab in the background, so the browser can ping before a meeting
      agendaPoll = setInterval(() => void loadAgenda(), AGENDA_MS);
      tick = setInterval(remind, 30_000);
      navigator.serviceWorker?.addEventListener('message', onAnswer);
    }
    return () => {
      clearInterval(poll);
      clearInterval(agendaPoll);
      clearInterval(tick);
      document.removeEventListener('visibilitychange', look);
      removeEventListener('popstate', onRoute);
      navigator.serviceWorker?.removeEventListener('message', onAnswer);
    };
  }, [p.api]);

  // the panel closes on a click outside or Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      btn.current?.focus();
    };
    addEventListener('pointerdown', onDown);
    addEventListener('keydown', onKey);
    return () => {
      removeEventListener('pointerdown', onDown);
      removeEventListener('keydown', onKey);
    };
  }, [open]);

  const follow = (n: Notice) => {
    setOpen(false);
    if (!n.read) void markRead([n.id]);
    if (p.follow && n.link) navigate(n.link);
    else if (n.link && p.onLink) p.onLink(n.link);
  };
  const now = new Date();
  const groups: ['today' | 'yesterday' | 'earlier', Notice[]][] = (['today', 'yesterday', 'earlier'] as const).map((g) => [g, items.filter((n) => dayGroup(n.at, now) === g)]);
  const time = (iso: string) =>
    dayGroup(iso, now) === 'earlier'
      ? new Intl.DateTimeFormat(lang === 'sv' ? 'sv-SE' : 'en-GB', { timeZone: 'Europe/Stockholm', day: 'numeric', month: 'short' }).format(new Date(iso))
      : new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Stockholm', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

  return (
    <>
      <div class="menu-wrap notify-wrap" ref={wrap}>
        <button type="button" class="icon-btn notify-btn" ref={btn} aria-haspopup="true" aria-expanded={open} aria-label={t.notify.bell(unread)} onClick={() => (setOpen(!open), !open && void load())}>
          <Icon name="bell" />
          {unread ? (
            <span class="notify-badge" aria-hidden="true">
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </button>
        {open ? (
          <div class="menu notify-panel" role="dialog" aria-label={t.notify.title}>
            <div class="notify-head">
              <strong>{t.notify.title}</strong>
              {unread ? (
                <button type="button" class="btn btn-ghost btn-sm" onClick={() => void markRead('all')}>
                  {t.notify.markAll}
                </button>
              ) : null}
            </div>
            {failed && !items.length ? <p class="notify-empty">{t.notify.failed}</p> : null}
            {!failed && !items.length ? <p class="notify-empty">{t.notify.empty}</p> : null}
            <div class="notify-list">
              {groups.map(([g, list]) =>
                list.length ? (
                  <div class="notify-group" key={g}>
                    <div class="menu-label">{t.notify[g]}</div>
                    <ul>
                      {list.map((n) => (
                        <li key={n.id}>
                          <button type="button" class={`notify-item${n.read ? '' : ' is-unread'}`} onClick={() => follow(n)}>
                            <span class="notify-dot" aria-hidden="true" />
                            <span class="notify-text">
                              <span class="notify-what">{n.text}</span>
                              {n.where ? <span class="notify-where">{n.where}</span> : null}
                            </span>
                            <time class="notify-time" dateTime={n.at}>
                              {time(n.at)}
                            </time>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null
              )}
            </div>
            {p.corner && pingNow !== 'none' ? (
              <div class="notify-ping">
                {pingNow === 'on' ? <span>{t.notify.pingOn}</span> : pingNow === 'blocked' ? <span>{t.notify.pingBlocked}</span> : (
                  <>
                    <span>{t.notify.pingOff}</span>
                    <button type="button" class="btn btn-sm" onClick={() => void allowPing()}>
                      {t.notify.pingAllow}
                    </button>
                  </>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {p.corner ? (
        <div class="notify-top" role="region" aria-label={t.notify.topRegion} aria-live="assertive">
          {boxes.map((b) =>
            b.kind === 'reminder' ? (
              <CornerBox
                key={b.key}
                box={b}
                close={() => (drop(b.key), void unping(b.key))}
                onOpen={() => (drop(b.key), void unping(b.key), b.item.link && navigate(b.item.link))}
                snooze={() => snooze(b.key)}
                allowPing={pingNow === 'off' ? () => void allowPing() : undefined}
              />
            ) : null
          )}
        </div>
      ) : null}
      {p.corner ? (
        <div class="notify-corner" role="region" aria-label={t.notify.region} aria-live="polite">
          {boxes.filter((b) => b.kind !== 'reminder').map((b) => (
            <CornerBox
              key={b.key}
              box={b}
              close={() => drop(b.key)}
              onOpen={() => {
                drop(b.key);
                if (b.kind === 'notice') follow(b.n);
                else if (b.kind === 'held') setOpen(true);
              }}
              snooze={() => drop(b.key)}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

const clock = (iso: string) => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Stockholm', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

function CornerBox(p: { box: Box; close: () => void; onOpen: () => void; snooze: () => void; allowPing?: () => void }) {
  const t = useT();
  const b = p.box;
  // notices go by themselves after a while (still in the bell); reminders stay until closed (Close, or the × top right: owner request 2026-10-19)
  useEffect(() => {
    if (b.kind === 'reminder') return;
    const id = setTimeout(p.close, SHOW_MS);
    return () => clearTimeout(id);
  }, []);
  if (b.kind === 'reminder') {
    const title = b.item.kind === 'shoot' ? t.notify.shootStartsIn(b.item.title, b.min) : t.notify.startsIn(b.item.title, b.min);
    return (
      <div class="notify-box is-reminder">
        <span class="notify-box-ic" aria-hidden="true">
          <Icon name={b.item.kind === 'shoot' ? 'film' : 'calendar'} />
        </span>
        <div class="notify-box-body">
          <strong>{title}</strong>
          <span class="notify-where">
            {clock(b.item.start)}–{clock(b.item.end)}
          </span>
          <div class="notify-box-acts">
            {b.item.meet ? (
              <a class="btn btn-primary btn-sm" href={b.item.meet} target="_blank" rel="noopener" onClick={p.close}>
                {t.notify.openMeet}
              </a>
            ) : b.item.link?.startsWith('/') ? (
              <button type="button" class="btn btn-primary btn-sm" onClick={p.onOpen}>
                {t.notify.open}
              </button>
            ) : null}
            <button type="button" class="btn btn-sm" onClick={p.snooze}>
              {t.notify.snooze}
            </button>
            <button type="button" class="btn btn-ghost btn-sm" onClick={p.close}>
              {t.notify.close}
            </button>
            {p.allowPing ? (
              <button type="button" class="btn btn-ghost btn-sm notify-ping-ask" onClick={p.allowPing}>
                {t.notify.pingAsk}
              </button>
            ) : null}
          </div>
        </div>
        <button type="button" class="notify-x" aria-label={t.notify.close} title={t.notify.close} onClick={p.close}>
          <Icon name="x" />
        </button>
      </div>
    );
  }
  return (
    <div class="notify-box">
      <span class="notify-box-ic" aria-hidden="true">
        <Icon name="bell" />
      </span>
      <div class="notify-box-body">
        <strong>{b.kind === 'held' ? t.notify.held(b.count) : b.n.text}</strong>
        {b.kind === 'notice' && b.n.where ? <span class="notify-where">{b.n.where}</span> : null}
        <div class="notify-box-acts">
          <button type="button" class="btn btn-sm" onClick={p.onOpen}>
            {t.notify.show}
          </button>
          <button type="button" class="btn btn-ghost btn-sm" onClick={p.close}>
            {t.notify.close}
          </button>
        </div>
      </div>
      <button type="button" class="notify-x" aria-label={t.notify.close} title={t.notify.close} onClick={p.close}>
        <Icon name="x" />
      </button>
    </div>
  );
}
